import {
  AlertCircle,
  AlertTriangle,
  ArrowUpRight,
  Info,
  RefreshCw,
  Sparkles,
  TrendingUp,
} from "lucide-react";
import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import type { AiLocationTile, AiRecommendation, AiSeverity } from "../lib/api";
import { POS_AI_RECOMMENDATIONS_VIEW } from "../lib/permissions";
import {
  fetchDashboardRecommendations,
  fetchLocationRecommendations,
  getPosClient,
  regenerateDashboardRecommendations,
  regenerateLocationRecommendations,
} from "../lib/api";
import { cn } from "../lib/utils";
import { usePageTitle } from "../lib/use-page-title";
import { FeedbackButtons } from "../components/insights/FeedbackButtons";

// ---------- severity config ----------

type Tone = "amber" | "emerald" | "sky";

const TONE: Record<AiSeverity, Tone> = {
  attention: "amber",
  opportunity: "emerald",
  info: "sky",
};

const SEVERITY_ORDER: Record<AiSeverity, number> = { attention: 0, opportunity: 1, info: 2 };

const CARD_BORDER: Record<AiSeverity, string> = {
  attention: "border-l-amber-400",
  opportunity: "border-l-emerald-400",
  info: "border-l-sky-400",
};

const KICKER: Record<AiSeverity, string> = {
  attention: "text-amber-600 dark:text-amber-400",
  opportunity: "text-emerald-700 dark:text-emerald-400",
  info: "text-sky-600 dark:text-sky-400",
};

const METRIC_CHIP: Record<Tone, string> = {
  amber: "bg-amber-100/70 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200",
  emerald: "bg-emerald-100/70 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200",
  sky: "bg-sky-100/70 text-sky-800 dark:bg-sky-900/40 dark:text-sky-200",
};

function SeverityIcon({ severity }: { readonly severity: AiSeverity }) {
  const cls = cn("h-3.5 w-3.5 shrink-0 mt-0.5", KICKER[severity]);
  if (severity === "attention") return <AlertTriangle className={cls} aria-hidden="true" />;
  if (severity === "opportunity") return <TrendingUp className={cls} aria-hidden="true" />;
  return <Info className={cls} aria-hidden="true" />;
}

// ---------- single recommendation card ----------

interface DashCardProps {
  readonly rec: AiRecommendation;
  readonly recommendationId?: string | undefined;
}

function DashCard({ rec, recommendationId }: DashCardProps) {
  const { t } = useTranslation();
  const tone = TONE[rec.severity];
  const kicker = t(`recommendations.severity.${rec.severity}`);

  return (
    <div
      className={cn(
        "flex items-start gap-3 rounded-xl border border-line border-l-4 bg-card px-4 py-3 shadow-elev-1",
        CARD_BORDER[rec.severity],
      )}
    >
      <SeverityIcon severity={rec.severity} />
      <div className="min-w-0 flex-1">
        <p
          className={cn(
            "mb-0.5 text-[10px] font-bold uppercase tracking-widest",
            KICKER[rec.severity],
          )}
        >
          {kicker}
        </p>
        {rec.title && (
          <p className="text-sm font-semibold text-strong leading-snug mb-0.5">{rec.title}</p>
        )}
        <p className="text-xs text-strong/80 leading-relaxed">{rec.body}</p>
        {(rec.metric || rec.cta_route) && (
          <div className="mt-2 flex flex-wrap items-center gap-2">
            {rec.metric && (
              <span
                className={cn(
                  "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold tabular-nums",
                  METRIC_CHIP[tone],
                )}
              >
                <span className="opacity-70">{rec.metric.label}:</span>
                <span>{rec.metric.value}</span>
              </span>
            )}
            {rec.cta_route && (
              <Link
                to={rec.cta_route}
                className="inline-flex items-center gap-0.5 text-[11px] font-medium text-primary hover:underline"
              >
                {t("recommendations.reviewCta")}
                <ArrowUpRight className="h-3 w-3" aria-hidden="true" />
              </Link>
            )}
          </div>
        )}
      </div>
      {recommendationId && (
        <div className="shrink-0">
          <FeedbackButtons recommendationId={recommendationId} />
        </div>
      )}
    </div>
  );
}

// ---------- single location tile card ----------

interface TileCardProps {
  readonly tile: AiLocationTile;
  readonly recommendationId?: string | undefined;
}

function TileCard({ tile, recommendationId }: TileCardProps) {
  const tone = TONE[tile.severity];

  return (
    <div
      className={cn(
        "flex items-start gap-3 rounded-xl border border-line border-l-4 bg-card px-4 py-3 shadow-elev-1",
        CARD_BORDER[tile.severity],
      )}
    >
      <SeverityIcon severity={tile.severity} />
      <div className="min-w-0 flex-1">
        <p className="text-xs text-strong/80 leading-relaxed">{tile.body}</p>
        {tile.metric_label && tile.metric_value && (
          <p
            className={cn(
              "mt-1 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold tabular-nums",
              METRIC_CHIP[tone],
            )}
          >
            <span className="opacity-70">{tile.metric_label}:</span>
            <span>{tile.metric_value}</span>
          </p>
        )}
      </div>
      {recommendationId && (
        <div className="shrink-0">
          <FeedbackButtons recommendationId={recommendationId} />
        </div>
      )}
    </div>
  );
}

// ---------- section wrapper ----------

function SectionHeader({
  title,
  renderedAt,
  regenerating,
  onRegenerate,
}: {
  readonly title: string;
  readonly renderedAt?: string | undefined;
  readonly regenerating: boolean;
  readonly onRegenerate: () => void;
}) {
  const { t } = useTranslation();
  const relative = renderedAt ? formatRelative(new Date(renderedAt), t) : null;

  return (
    <div className="flex items-center justify-between gap-2">
      <div className="flex items-center gap-2">
        <h2 className="text-sm font-semibold text-strong">{title}</h2>
        {relative && (
          <span className="flex items-center gap-1 text-[10px] text-muted">
            <Sparkles className="h-2.5 w-2.5 shrink-0" aria-hidden="true" />
            {relative}
          </span>
        )}
      </div>
      <button
        type="button"
        onClick={onRegenerate}
        disabled={regenerating}
        className={cn(
          "flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs font-medium text-muted transition-colors",
          "hover:bg-surface-muted hover:text-strong disabled:opacity-40 disabled:cursor-not-allowed",
        )}
        aria-label={t("recommendations.refreshAriaLabel")}
      >
        <RefreshCw
          className={cn("h-3.5 w-3.5", regenerating && "animate-spin")}
          aria-hidden="true"
        />
        {t("recommendations.refresh")}
      </button>
    </div>
  );
}

// ---------- not-entitled empty state ----------

function NotEntitledCard() {
  const { t } = useTranslation();
  return (
    <div className="flex items-center gap-3 rounded-xl border border-dashed border-line bg-surface px-4 py-6 text-center">
      <div className="mx-auto flex flex-col items-center gap-2 text-muted">
        <Sparkles className="h-6 w-6 opacity-40" aria-hidden="true" />
        <p className="text-xs text-muted">{t("recommendations.notEntitled")}</p>
      </div>
    </div>
  );
}

// ---------- error state ----------

function ErrorCard({ onRetry }: { readonly onRetry: () => void }) {
  const { t } = useTranslation();
  return (
    <div className="flex items-center gap-3 rounded-xl border border-line bg-surface px-4 py-4">
      <AlertCircle className="h-4 w-4 shrink-0 text-destructive" aria-hidden="true" />
      <p className="flex-1 text-xs text-strong/80">{t("recommendations.errorState")}</p>
      <button
        type="button"
        onClick={onRetry}
        className="text-xs font-medium text-primary hover:underline"
      >
        {t("recommendations.refresh")}
      </button>
    </div>
  );
}

// ---------- page ----------

type Period = "daily" | "weekly" | "monthly";

const PERIODS: Period[] = ["daily", "weekly", "monthly"];

export function RecommendationsPage() {
  const { t } = useTranslation();
  usePageTitle("insights");

  const qc = useQueryClient();
  const [period, setPeriod] = useState<Period>("daily");
  const [dashRegen, setDashRegen] = useState(false);
  const [locRegen, setLocRegen] = useState(false);

  const profile = useQuery({
    queryKey: ["profile"],
    queryFn: () => getPosClient().getProfile(),
    staleTime: Infinity,
  });

  const locationId = profile.data?.active_membership?.location?.id ?? undefined;
  const isLocationScope = profile.data?.active_membership?.scope_kind === "location";
  const hasViewPerm =
    profile.isSuccess && new Set(profile.data?.permissions ?? []).has(POS_AI_RECOMMENDATIONS_VIEW);

  const dash = useQuery({
    queryKey: ["ai-recommendations", "dashboard", period, locationId],
    queryFn: () => fetchDashboardRecommendations(period, locationId),
    staleTime: (query) => {
      const d = query.state.data;
      if (!d?.expires_at) return 0;
      return Math.max(0, new Date(d.expires_at).getTime() - Date.now());
    },
    retry: false,
    refetchOnWindowFocus: false,
    enabled: hasViewPerm,
  });

  const loc = useQuery({
    queryKey: ["ai-recommendations", "location", locationId],
    queryFn: () => fetchLocationRecommendations(locationId!),
    staleTime: (query) => {
      const d = query.state.data;
      if (!d?.expires_at) return 0;
      return Math.max(0, new Date(d.expires_at).getTime() - Date.now());
    },
    retry: false,
    refetchOnWindowFocus: false,
    enabled: hasViewPerm && !!locationId && isLocationScope,
  });

  async function handleDashRegenerate() {
    setDashRegen(true);
    try {
      await regenerateDashboardRecommendations(period, locationId);
      await qc.invalidateQueries({
        queryKey: ["ai-recommendations", "dashboard", period, locationId],
      });
    } finally {
      setDashRegen(false);
    }
  }

  async function handleLocRegenerate() {
    if (!locationId) return;
    setLocRegen(true);
    try {
      await regenerateLocationRecommendations(locationId);
      await qc.invalidateQueries({ queryKey: ["ai-recommendations", "location", locationId] });
    } finally {
      setLocRegen(false);
    }
  }

  const dashRecs = [...(dash.data?.recommendations ?? [])].sort(
    (a, b) => SEVERITY_ORDER[a.severity] - SEVERITY_ORDER[b.severity],
  );

  const locTiles = [...(loc.data?.tiles ?? [])].sort(
    (a, b) => SEVERITY_ORDER[a.severity] - SEVERITY_ORDER[b.severity],
  );

  return (
    <div className="flex flex-col gap-6">
      {/* Page header */}
      <div className="flex flex-col gap-1">
        <div className="flex items-center gap-2">
          <span
            className="flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-br from-violet-600 to-indigo-500"
            aria-hidden="true"
          >
            <Sparkles className="h-4 w-4 text-white" />
          </span>
          <h1 className="text-xl font-bold text-strong">{t("recommendations.title")}</h1>
        </div>
        <p className="text-sm text-muted pl-10">{t("recommendations.subtitle")}</p>
      </div>

      {/* Period selector */}
      <div className="flex gap-1 rounded-lg bg-surface-muted p-1 w-fit">
        {PERIODS.map((p) => (
          <button
            key={p}
            type="button"
            onClick={() => setPeriod(p)}
            className={cn(
              "rounded-md px-4 py-1.5 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary",
              period === p ? "bg-card text-strong shadow-elev-1" : "text-muted hover:text-strong",
            )}
          >
            {t(`recommendations.period${p.charAt(0).toUpperCase()}${p.slice(1)}`)}
          </button>
        ))}
      </div>

      {/* Shift Insights section */}
      <section className="flex flex-col gap-3">
        <SectionHeader
          title={t("recommendations.shiftInsights")}
          renderedAt={dash.data?.rendered_at}
          regenerating={dashRegen || dash.isFetching}
          onRegenerate={() => {
            void handleDashRegenerate();
          }}
        />

        {dash.isLoading && (
          <div className="flex flex-col gap-2">
            {[0, 1, 2].map((i) => (
              <div
                key={i}
                className="h-[72px] animate-pulse rounded-xl bg-surface-muted"
                style={{ animationDelay: `${i * 80}ms` }}
              />
            ))}
          </div>
        )}

        {dash.isError && <ErrorCard onRetry={() => void dash.refetch()} />}

        {!dash.isLoading && !dash.isError && dash.data === null && <NotEntitledCard />}

        {!dash.isLoading && !dash.isError && dash.data != null && dashRecs.length === 0 && (
          <div className="rounded-xl border border-dashed border-line bg-surface px-4 py-6 text-center">
            <p className="text-sm text-muted">{t("recommendations.noInsights")}</p>
          </div>
        )}

        {dashRecs.length > 0 && (
          <div className="flex flex-col gap-2">
            {dashRecs.map((rec) => (
              <DashCard key={rec.id} rec={rec} recommendationId={dash.data?.recommendation_id} />
            ))}
          </div>
        )}
      </section>

      {/* Location Insights section - only when in location scope */}
      {isLocationScope && locationId && (
        <section className="flex flex-col gap-3">
          <SectionHeader
            title={t("recommendations.locationInsights")}
            renderedAt={loc.data?.rendered_at}
            regenerating={locRegen || loc.isFetching}
            onRegenerate={() => {
              void handleLocRegenerate();
            }}
          />

          {loc.isLoading && (
            <div className="flex flex-col gap-2">
              {[0, 1].map((i) => (
                <div
                  key={i}
                  className="h-[60px] animate-pulse rounded-xl bg-surface-muted"
                  style={{ animationDelay: `${i * 80}ms` }}
                />
              ))}
            </div>
          )}

          {loc.isError && <ErrorCard onRetry={() => void loc.refetch()} />}

          {!loc.isLoading && !loc.isError && loc.data === null && <NotEntitledCard />}

          {!loc.isLoading && !loc.isError && loc.data != null && locTiles.length === 0 && (
            <div className="rounded-xl border border-dashed border-line bg-surface px-4 py-6 text-center">
              <p className="text-sm text-muted">{t("recommendations.noInsights")}</p>
            </div>
          )}

          {locTiles.length > 0 && (
            <div className="flex flex-col gap-2">
              {locTiles.map((tile) => (
                <TileCard
                  key={tile.id}
                  tile={tile}
                  recommendationId={loc.data?.recommendation_id}
                />
              ))}
            </div>
          )}
        </section>
      )}
    </div>
  );
}

// ---------- helpers ----------

function formatRelative(date: Date, t: ReturnType<typeof useTranslation>["t"]): string {
  const diffMs = Date.now() - date.getTime();
  const diffMin = Math.round(diffMs / 60_000);
  if (diffMin < 2) return t("recommendations.justNow");
  if (diffMin < 60) return t("recommendations.minutesAgo", { count: diffMin });
  const diffH = Math.round(diffMin / 60);
  if (diffH < 24) return t("recommendations.hoursAgo", { count: diffH });
  return date.toLocaleDateString();
}
